import React, { useState, useRef } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { getDataFromAPI } from "../../../HandleAPICalls/actions";
import { toastr } from "react-redux-toastr";
import NavigationalCreateComponent from "./component";

const NavigationalCreateContainer = (props) => {
  const navigate = useNavigate();
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    active: true,
  });

  const transparentPixel =
    "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8/5+hHgAHggJ/PchI7wAAAABJRU5ErkJggg==";

  const [iconFile, setIconFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const [iconPreview, setIconPreview] = useState(null);
  const [bgPreview, setBgPreview] = useState(null);
  const [useTransparentBg, setUseTransparentBg] = useState(false);
  const [verifiedVariants, setVerifiedVariants] = useState([]);
  const [variantDetails, setVariantDetails] = useState({});
  const [inputValue, setInputValue] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isIconUploading, setIsIconUploading] = useState(false);
  const [isBgUploading, setIsBgUploading] = useState(false);
  const [iconPath, setIconPath] = useState("");
  const [bgPath, setBgPath] = useState("");
  const [createdItemName, setCreatedItemName] = useState("");
  const [createdItemId, setCreatedItemId] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  const iconInputRef = useRef(null);
  const backgroundInputRef = useRef(null);

  const isFormValid =
    formData.name.trim() !== "" &&
    iconPath !== "" &&
    (bgPath !== "" || useTransparentBg) &&
    verifiedVariants.length > 0;

  // Machine Image Upload API
  const uploadImageToS3 = async (file, type) => {
    if (!file) return null;
    
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);
    
    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v2/s3upload/machine-image",
        "POST",
        formDataUpload,
        (response) => {
          console.log(`${type} upload response:`, response);
          // Extract image path from response
          const uploadedPath = response?.image_path || response?.data?.image_path || response?.path || response?.url;
          
          if (!uploadedPath) {
            reject(new Error("No image path received from upload API"));
            return;
          }
          
          toastr.success("Success", `${type} uploaded successfully`);
          resolve(uploadedPath);
        },
        (err) => {
          console.error(`${type} upload error:`, err);
          toastr.error("Failed", `Unable to upload ${type.toLowerCase()}`);
          reject(err);
        }
      );
    });
  };

  // Handle Icon Selection - Upload to S3 first
  const handleIconFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.match('image.*')) {
      toastr.error("Error", "Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toastr.error("Error", "Image size should be less than 5MB");
      return;
    }
    
    setIsIconUploading(true);
    setIconFile(file);
    
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setIconPreview(reader.result);
    reader.readAsDataURL(file);
    
    try {
      // Upload to S3 and get path
      const uploadedPath = await uploadImageToS3(file, "Icon");
      setIconPath(uploadedPath);
      console.log("Icon uploaded to:", uploadedPath);
    } catch (error) {
      console.error("Icon upload failed:", error);
      // Clear preview on error
      setIconPreview(null);
      setIconFile(null);
    } finally {
      setIsIconUploading(false);
    }
  };

  // Handle Background Selection - Upload to S3 first
  const handleBgFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file
    if (!file.type.match('image.*')) {
      toastr.error("Error", "Please upload an image file");
      return;
    }
    
    if (file.size > 5 * 1024 * 1024) {
      toastr.error("Error", "Image size should be less than 5MB");
      return;
    }
    
    setIsBgUploading(true);
    setBgFile(file);
    
    // Show preview immediately
    const reader = new FileReader();
    reader.onloadend = () => setBgPreview(reader.result);
    reader.readAsDataURL(file);
    
    try {
      // Upload to S3 and get path
      const uploadedPath = await uploadImageToS3(file, "Background");
      setBgPath(uploadedPath);
      console.log("Background uploaded to:", uploadedPath);
    } catch (error) {
      console.error("Background upload failed:", error);
      // Clear preview on error
      setBgPreview(null);
      setBgFile(null);
    } finally {
      setIsBgUploading(false);
    }
  };

  const handleClearIcon = () => {
    setIconFile(null);
    setIconPreview(null);
    setIconPath("");
    if (iconInputRef.current) iconInputRef.current.value = "";
  };

  const handleClearBg = () => {
    setBgFile(null);
    setBgPreview(null);
    setBgPath("");
    if (backgroundInputRef.current) backgroundInputRef.current.value = "";
  };

  const handleToggleTransparent = () => {
    const newVal = !useTransparentBg;
    setUseTransparentBg(newVal);
    if (newVal) {
      setBgPath("");
      setBgPreview(null);
      setBgFile(null);
      toastr.info("Info", "Transparent background selected");
    } else {
      setBgPath("");
      setBgPreview(null);
      setBgFile(null);
    }
  };

  // Check Variant IDs via API
  const handleCheckVariants = async (idsToCheck) => {
    setIsVerifying(true);
    const requestBody = idsToCheck.map(id => ({ variantId: parseInt(id) || id }));
    
    return new Promise((resolve, reject) => {
      props.getDataFromAPI(
        "/partner/api/v6/product/variant/check",
        "POST",
        requestBody,
        (response) => {
          setIsVerifying(false);
          const data = response?.data || response || [];
          const validVariantIds = [];
          const invalidVariantIds = [];
          const detailsMap = {};
          
          data.forEach(item => {
            const variantId = item.variantId;
            if (item.status === true) {
              validVariantIds.push(variantId);
              detailsMap[variantId] = {
                status: item.status,
                image: item.image || null,
                variantId: variantId
              };
            } else {
              invalidVariantIds.push(variantId);
            }
          });
          
          resolve({ validVariantIds, invalidVariantIds, detailsMap });
        },
        (err) => {
          setIsVerifying(false);
          toastr.error("Verification Failed", "Unable to verify variant IDs");
          reject(err);
        }
      );
    });
  };

  // Add ID with Verification
  const handleAddId = async () => {
    if (inputValue.trim()) {
      const ids = inputValue.split(/[\s,]+/).filter(id => id.trim() && !verifiedVariants.includes(id));
      
      if (ids.length > 0) {
        try {
          const { validVariantIds, invalidVariantIds, detailsMap } = await handleCheckVariants(ids);
          
          if (invalidVariantIds.length > 0) {
            toastr.warning("Invalid Variants", `${invalidVariantIds.length} variant(s) are invalid: ${invalidVariantIds.join(", ")}`);
          }
          
          if (validVariantIds.length > 0) {
            const newValidIds = validVariantIds.filter(id => !verifiedVariants.includes(id.toString()));
            const newDetails = { ...variantDetails };
            newValidIds.forEach(id => {
              if (detailsMap[id]) newDetails[id] = detailsMap[id];
            });
            setVariantDetails(newDetails);
            setVerifiedVariants([...verifiedVariants, ...newValidIds]);
            toastr.success("Success", `${newValidIds.length} variant(s) verified successfully`);
          }
          setInputValue("");
        } catch (error) {
          console.error("Error verifying variants:", error);
        }
      }
      setInputValue("");
    }
  };

  const removeId = (idToRemove) => {
    setVerifiedVariants(verifiedVariants.filter(id => id !== idToRemove));
    const newDetails = { ...variantDetails };
    delete newDetails[idToRemove];
    setVariantDetails(newDetails);
  };

  const handleClearAllVariants = () => {
    setVerifiedVariants([]);
    setVariantDetails({});
    toastr.info("Cleared", "All variants cleared");
  };

  const handleBackClick = () => navigate(-1);

  const handleSave = async () => {
    if (!isFormValid) {
      toastr.warning("Incomplete Form", "Please fill all required fields");
      return;
    }
    
    setIsSubmitting(true);

    // Prepare payload with uploaded image paths
    const payload = {
      name: formData.name,
      description: formData.description || "",
      imagePath: iconPath, // This is the path from S3 upload API
      bgImagePath: useTransparentBg ? transparentPixel : bgPath, // This is the path from S3 upload API
      bgTransparent: useTransparentBg,
      active: formData.active,
      variantIds: verifiedVariants.map(id => parseInt(id) || id),
    };

    console.log("Create Payload:", payload);

    props.getDataFromAPI(
      `/partner/api/v1/nav-categories`,
      "POST",
      payload,
      (response) => {
        setIsSubmitting(false);
        console.log("Create response:", response);
        const newId = response?.id || response?.data?.id || "N/A";
        const newName = response?.name || response?.data?.name || formData.name;
        setCreatedItemName(newName);
        setCreatedItemId(newId);
        setShowSuccessPopup(true);
        toastr.success("Success", "Navigational created successfully!");
      },
      (error) => {
        setIsSubmitting(false);
        console.error("Create error:", error);
        toastr.error("Error", error?.message || "Failed to create navigational");
      }
    );
  };

  const handleCloseSuccessPopup = () => {
    setShowSuccessPopup(false);
    navigate(-1);
  };

  return (
    <NavigationalCreateComponent
      formData={formData}
      setFormData={setFormData}
      iconPreview={iconPreview}
      bgPreview={bgPreview}
      useTransparentBg={useTransparentBg}
      variantIds={verifiedVariants}
      variantDetails={variantDetails}
      inputValue={inputValue}
      setInputValue={setInputValue}
      iconInputRef={iconInputRef}
      backgroundInputRef={backgroundInputRef}
      isFormValid={isFormValid}
      isSubmitting={isSubmitting}
      isIconUploading={isIconUploading}
      isBgUploading={isBgUploading}
      showSuccessPopup={showSuccessPopup}
      setShowSuccessPopup={handleCloseSuccessPopup}
      handleToggleTransparent={handleToggleTransparent}
      handleAddId={handleAddId}
      removeId={removeId}
      handleClearAllVariants={handleClearAllVariants}
      handleBackClick={handleBackClick}
      handleSave={handleSave}
      handleIconFileSelect={handleIconFileSelect}
      handleBgFileSelect={handleBgFileSelect}
      handleClearIcon={handleClearIcon}
      handleClearBg={handleClearBg}
      iconPath={iconPath}
      bgPath={bgPath}
      createdItemName={createdItemName}
      createdItemId={createdItemId}
      isVerifying={isVerifying}
      verifiedVariantsCount={verifiedVariants.length}
    />
  );
};

function mapStateToProps(state) {
  return { props: state };
}

export default connect(mapStateToProps, { getDataFromAPI })(NavigationalCreateContainer);